// SPDX-License-Identifier: AGPL-3.0-or-later

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import Users from '@app/features/user/state/Users';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useMemo} from 'react';

export type UnaddableRecipientReason = 'unknown_user' | 'blocked' | 'not_friends' | 'group_dm_add_disabled';

export interface UnaddableRecipient {
	userId: string;
	reason: UnaddableRecipientReason;
}

interface UnaddableRecipientsConfirmModalProps {
	unaddableRecipients: Array<UnaddableRecipient>;
	addableCount: number;
	onConfirm: () => Promise<void> | void;
}

function useReasonLabel(): (reason: UnaddableRecipientReason) => string {
	const {t} = useLingui();
	return (reason) => {
		switch (reason) {
			case 'unknown_user':
				return t`User not found`;
			case 'blocked':
				return t`You can't message this user`;
			case 'not_friends':
				return t`Not on your friends list`;
			case 'group_dm_add_disabled':
				return t`Doesn't allow being added to group DMs`;
		}
	};
}

export const UnaddableRecipientsConfirmModal = observer(
	({unaddableRecipients, addableCount, onConfirm}: UnaddableRecipientsConfirmModalProps) => {
		const {t} = useLingui();
		const reasonLabel = useReasonLabel();
		const rows = useMemo(
			() =>
				unaddableRecipients.map(({userId, reason}) => {
					const user = Users.getUser(userId);
					const name = user?.displayName ?? userId;
					return {userId, name, reasonText: reasonLabel(reason)};
				}),
			[unaddableRecipients, reasonLabel],
		);
		const description = (
			<>
				<p>
					<Trans>The following people can't be added to this group DM:</Trans>
				</p>
				<ul>
					{rows.map((row) => (
						<li key={row.userId}>
							<strong>{row.name}</strong> — {row.reasonText}
						</li>
					))}
				</ul>
				{addableCount > 0 ? (
					<p>
						<Trans>Create the group DM with the remaining {addableCount} recipient(s) and skip the others?</Trans>
					</p>
				) : (
					<p>
						<Trans>No remaining recipients to create a group DM with.</Trans>
					</p>
				)}
			</>
		);
		if (addableCount === 0) {
			return (
				<ConfirmModal
					title={t`Some users can't be added`}
					description={description}
					secondaryText={t`Close`}
					size="small"
				/>
			);
		}
		return (
			<ConfirmModal
				title={t`Some users can't be added`}
				description={description}
				primaryText={t`Create without them`}
				primaryVariant="primary"
				secondaryText={t`Cancel`}
				size="small"
				onPrimary={onConfirm}
			/>
		);
	},
);
